import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, X, Upload, FileText, Eye, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const resolveImg = (src) => (src?.startsWith("http") ? src : `${API}/files/${src}`);
const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

const EMPTY = {
  title: "", slug: "", excerpt: "", cover_image: "", content: "",
  pdf_path: "", pdf_filename: "", download_heading: "", download_blurb: "",
  download_button_label: "", status: "draft", order: 0,
};

const FORMAT_HELP = `Formatting guide:
## Big heading   ·   ### Smaller heading
**bold text**   ·   [link text](https://url.com)
- bullet item (one per line)
1. numbered item (one per line)
![image description](paste-image-url)   ← use "Insert image" button
[[button:Button Label|https://url.com]]   ← a CTA button
Leave a blank line between paragraphs.`;

export default function AdminResources() {
  const { user } = useAuth();
  const [tab, setTab] = useState("resources");
  const [resources, setResources] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState("");
  const coverRef = useRef();
  const pdfRef = useRef();
  const inlineImgRef = useRef();
  const contentRef = useRef();

  const load = async () => {
    const [{ data: rs }, { data: dl }] = await Promise.all([
      api.get("/admin/resources"),
      api.get("/admin/resource-downloads"),
    ]);
    setResources(rs);
    setDownloads(dl.downloads || []);
  };
  useEffect(() => { load(); }, []);

  if (!user || user.role !== "admin")
    return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Admin only.</div>;

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (r) => { setEditing(r.id); setForm({ ...EMPTY, ...r }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.path;
  };

  const onCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading("cover");
    try {
      const path = await uploadImage(file);
      setForm((f) => ({ ...f, cover_image: path }));
      toast.success("Cover uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(""); }
  };

  const onInlineImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading("inline");
    try {
      const path = await uploadImage(file);
      setForm((f) => ({ ...f, content: `${f.content}${f.content ? "\n\n" : ""}![${file.name}](${path})` }));
      toast.success("Image added to article content");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(""); }
  };

  const onPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading("pdf");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/resources/upload-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, pdf_path: data.path, pdf_filename: data.filename }));
      toast.success("PDF uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(""); }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, slug: form.slug || slugify(form.title), order: parseInt(form.order, 10) || 0 };
    try {
      if (editing === "new") { await api.post("/admin/resources", payload); toast.success("Resource created"); }
      else { await api.put(`/admin/resources/${editing}`, payload); toast.success("Resource updated"); }
      cancel();
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this resource? This cannot be undone.")) return;
    await api.delete(`/admin/resources/${id}`);
    toast.success("Resource deleted");
    load();
  };

  const togglePublish = async (r) => {
    await api.put(`/admin/resources/${r.id}`, { status: r.status === "published" ? "draft" : "published" });
    load();
  };

  // group downloads by email
  const byEmail = {};
  downloads.forEach((d) => {
    (byEmail[d.email] = byEmail[d.email] || { email: d.email, first_name: d.first_name, items: [] }).items.push(d);
  });
  const people = Object.values(byEmail).sort((a, b) =>
    (b.items[0]?.created_at || "").localeCompare(a.items[0]?.created_at || ""));

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return iso; } };

  return (
    <div data-testid="admin-resources" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-3">◦ Admin · Seller Resources</div>
          <h1 className="font-serif text-4xl lg:text-5xl tracking-tight">Seller Resources</h1>
          <p className="text-muted-foreground mt-2 text-sm">Guides and downloadable resources for handmade sellers.</p>
        </div>
        {tab === "resources" && !editing && (
          <button onClick={startNew} data-testid="add-resource-btn" className="btn-primary px-5 py-3 text-sm rounded-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> New resource
          </button>
        )}
      </div>

      {!editing && (
        <div className="flex gap-6 border-b border-border mb-8">
          {[["resources", "Resources"], ["downloads", `Downloads (${downloads.length})`]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`resources-tab-${k}`}
              className={`pb-3 text-sm tracking-wide -mb-px border-b-2 transition-colors ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* EDIT FORM */}
      {editing && (
        <form onSubmit={save} className="border border-border p-8 rounded-sm bg-card mb-10">
          <div className="flex justify-between items-center mb-8">
            <h2 className="font-serif text-2xl">{editing === "new" ? "New resource" : "Edit resource"}</h2>
            <button type="button" onClick={cancel} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Title</label>
              <input data-testid="res-title" required value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })}
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Slug (URL)</label>
              <input data-testid="res-slug" value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
              <p className="text-xs text-muted-foreground mt-1">/resources/{form.slug || "your-slug"}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Status</label>
                <select data-testid="res-status" value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Order</label>
                <input data-testid="res-order" type="number" value={form.order}
                  onChange={(e) => setForm({ ...form, order: e.target.value })}
                  className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Short description (excerpt)</label>
              <textarea data-testid="res-excerpt" rows={2} value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors resize-none" />
            </div>

            {/* Cover image */}
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 block">Cover image</label>
              <div className="flex items-center gap-4">
                <div className="w-28 h-20 bg-secondary overflow-hidden rounded-sm flex items-center justify-center flex-shrink-0">
                  {form.cover_image ? <img src={resolveImg(form.cover_image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-muted-foreground" />}
                </div>
                <input ref={coverRef} type="file" accept="image/*" onChange={onCover} className="hidden" />
                <button type="button" data-testid="res-cover-upload" onClick={() => coverRef.current?.click()}
                  className="border border-border px-4 py-2 text-sm rounded-sm hover:bg-secondary inline-flex items-center gap-2">
                  {uploading === "cover" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {form.cover_image ? "Replace cover" : "Upload cover"}
                </button>
                {form.cover_image && <button type="button" onClick={() => setForm({ ...form, cover_image: "" })} className="text-xs text-muted-foreground hover:text-destructive">Remove</button>}
              </div>
            </div>

            {/* PDF */}
            <div className="md:col-span-2">
              <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 block">Downloadable PDF</label>
              <div className="flex items-center gap-4">
                <input ref={pdfRef} type="file" accept="application/pdf" onChange={onPdf} className="hidden" />
                <button type="button" data-testid="res-pdf-upload" onClick={() => pdfRef.current?.click()}
                  className="border border-border px-4 py-2 text-sm rounded-sm hover:bg-secondary inline-flex items-center gap-2">
                  {uploading === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  {form.pdf_path ? "Replace PDF" : "Upload PDF"}
                </button>
                {form.pdf_path && (
                  <span className="text-sm text-muted-foreground inline-flex items-center gap-2">
                    {form.pdf_filename || "resource.pdf"}
                    <button type="button" onClick={() => setForm({ ...form, pdf_path: "", pdf_filename: "" })} className="text-xs hover:text-destructive">Remove</button>
                  </span>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Full article content</label>
                <div>
                  <input ref={inlineImgRef} type="file" accept="image/*" onChange={onInlineImage} className="hidden" />
                  <button type="button" data-testid="res-insert-image" onClick={() => inlineImgRef.current?.click()}
                    className="text-xs border border-border px-3 py-1.5 rounded-sm hover:bg-secondary inline-flex items-center gap-1.5">
                    {uploading === "inline" ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />} Insert image
                  </button>
                </div>
              </div>
              <textarea ref={contentRef} data-testid="res-content" rows={14} value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="w-full bg-transparent border border-border focus:border-primary outline-none p-4 mt-1 rounded-sm transition-colors font-mono text-sm leading-relaxed" />
              <pre className="text-[11px] text-muted-foreground mt-2 whitespace-pre-wrap leading-relaxed">{FORMAT_HELP}</pre>
            </div>

            {/* Download block customization */}
            <div className="md:col-span-2 border-t border-border pt-6">
              <div className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">◦ Download section (optional copy)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Heading</label>
                  <input data-testid="res-dl-heading" value={form.download_heading} placeholder="Want the printable version?"
                    onChange={(e) => setForm({ ...form, download_heading: e.target.value })}
                    className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Button label</label>
                  <input data-testid="res-dl-button" value={form.download_button_label} placeholder="Get the Free Checklist"
                    onChange={(e) => setForm({ ...form, download_button_label: e.target.value })}
                    className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Blurb</label>
                  <textarea data-testid="res-dl-blurb" rows={2} value={form.download_blurb} placeholder="Download the free checklist and work through your plan step by step."
                    onChange={(e) => setForm({ ...form, download_blurb: e.target.value })}
                    className="w-full bg-transparent border-b border-border focus:border-primary outline-none py-3 mt-2 transition-colors resize-none" />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex gap-3 flex-wrap">
            <button data-testid="save-resource-btn" className="btn-primary px-6 py-3 text-sm rounded-sm">Save resource</button>
            {form.slug && (
              <a href={`/resources/${form.slug}?preview=1`} target="_blank" rel="noopener noreferrer"
                 data-testid="preview-resource-btn"
                 className="border border-border px-6 py-3 text-sm rounded-sm hover:bg-secondary transition-colors inline-flex items-center gap-2">
                <Eye className="w-4 h-4" /> Preview
              </a>
            )}
            <button type="button" onClick={cancel} className="border border-border px-6 py-3 text-sm rounded-sm hover:bg-secondary transition-colors">Cancel</button>
          </div>
        </form>
      )}

      {/* RESOURCES LIST */}
      {!editing && tab === "resources" && (resources.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground border border-border rounded-sm">
          No resources yet. Create your first guide.
        </div>
      ) : (
        <div className="space-y-4">
          {resources.map((r) => (
            <div key={r.id} className="border border-border p-6 rounded-sm bg-card flex gap-6 items-center">
              <div className="w-24 h-20 bg-secondary overflow-hidden rounded-sm flex-shrink-0 flex items-center justify-center">
                {r.cover_image ? <img src={resolveImg(r.cover_image)} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-5 h-5 text-muted-foreground" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-serif text-xl">{r.title}</h3>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${r.status === "published" ? "border-primary/40 text-primary" : "border-border text-muted-foreground"}`}>
                    {r.status}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  /{r.slug} · order {r.order} · {r.download_count} download{r.download_count === 1 ? "" : "s"} · {r.pdf_path ? "PDF attached" : "no PDF"}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-wrap justify-end">
                <a href={`/resources/${r.slug}?preview=1`} target="_blank" rel="noopener noreferrer" className="text-xs text-muted-foreground hover:text-primary px-2 inline-flex items-center gap-1"><Eye className="w-3 h-3" /> Preview</a>
                <button onClick={() => togglePublish(r)} data-testid={`toggle-publish-${r.slug}`} className="border border-border px-3 py-2 text-xs hover:bg-secondary rounded-sm">
                  {r.status === "published" ? "Unpublish" : "Publish"}
                </button>
                <button onClick={() => startEdit(r)} className="border border-border px-3 py-2 text-xs hover:bg-secondary rounded-sm inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
                <button onClick={() => del(r.id)} className="border border-border p-2 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive rounded-sm"><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* DOWNLOADS LIST */}
      {!editing && tab === "downloads" && (people.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground border border-border rounded-sm">
          No downloads yet.
        </div>
      ) : (
        <div className="space-y-4" data-testid="downloads-list">
          {people.map((p) => (
            <div key={p.email} className="border border-border p-6 rounded-sm bg-card">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{p.first_name}</div>
                  <div className="text-sm text-muted-foreground">{p.email}</div>
                </div>
                <div className="text-xs text-muted-foreground">{p.items.length} download{p.items.length === 1 ? "" : "s"}</div>
              </div>
              <ul className="mt-4 space-y-1.5 border-t border-border pt-4">
                {p.items.map((it) => (
                  <li key={it.id} className="text-sm flex justify-between">
                    <span>{it.resource_title}</span>
                    <span className="text-muted-foreground">{fmtDate(it.created_at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
